import { computeRowProps } from "../yield-sheet/rowProp";
import React, { Component } from "react";
import { toast } from "react-toastify";
import { YieldSheet } from "../yield-sheet/yield-sheet";
import DatePicker from "react-datepicker";
import { auth } from "gn-shared";
import SheetView from "../common/sheetView";
import {
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  CircularProgress,
  Backdrop,
  Tooltip,
  ThemeProvider,
  Button,
  ButtonGroup,
} from "@mui/material";
import {
  Edit,
  ArrowDropDown,
  EventRepeat,
  ArrowBackIos,
  ArrowForwardIos,
  RoomService,
} from "@mui/icons-material";

import { baseTheme } from "../../mui-theme";

import { IDashboardState } from "./defs";
import { CmApi } from "../../api/cm";
import withRouter from "../withRouter";
import {
  format,
  endOfMonth,
  addMonths,
  startOfMonth,
  addDays,
  addYears,
  isBefore,
  isValid,
  differenceInDays,
} from "date-fns";
import BulkUpdate from "../bulk-update/bulk-update";
import { YieldApi } from "api/yield";
import { connect } from "react-redux";
import { setActiveHotel } from "redux/Slice/hotelSlice";
import { resetYieldsheet } from "redux/Slice/yieldssheetSlice";
import { AuthService } from "App/services";
import { ACTIONS, hasPermission } from "App/common/permissionHandler";
import { ConfigApi } from "api/config";

class Dashboard extends Component<
  {
    router: {
      location: {
        pathname: string;
      };
      navigate: (path: string) => void;
    };
    activeHotelId: number;
    activeHotel: any;
    user: any;
    setActiveHotel: any;
    resetYieldsheet: any;
  },
  IDashboardState
> {
  _isMounted = false;
  readonly state: IDashboardState = {
    dateFrom: new Date(),
    dateTo: endOfMonth(new Date()),
    yieldSheetPending: false,
    publishDropdownShown: false,
    selectedHotel: {},
    sheetView: null,
    isEdited: false,
    counter: 0,
    openBulkUpdate: false,
    lastCalendarDate: format(addYears(new Date(), 2), "yyyy-MM-dd"),
  };
  private anchorRef = React.createRef<HTMLDivElement>();
  debounceTimeout: NodeJS.Timeout | null = null;

  // Helper function for async delay
  delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

  handleToggleDropdown = () => {
    this.setState((prevState) => ({
      publishDropdownShown: !prevState.publishDropdownShown,
    }));
  };

  handleCloseDropdown = (event: Event) => {
    if (
      this.anchorRef.current &&
      this.anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    this.setState({ publishDropdownShown: false });
  };

  constructor(props: any) {
    super(props);
    this.props.resetYieldsheet();
  }

  calculateDateTo = (dateFrom: Date, dateTo: Date): Date => {
    if (isBefore(new Date(this.state.lastCalendarDate), dateTo)) {
      return new Date(this.state.lastCalendarDate);
    }
    if (differenceInDays(endOfMonth(dateFrom), dateFrom) < 15) {
      return dateTo;
    }
    if (
      differenceInDays(dateTo, dateFrom) > 31 ||
      differenceInDays(dateTo, dateFrom) < 0
    ) {
      return addDays(dateFrom, 31);
    }
    return dateTo;
  };

  async componentDidMount() {
    const { activeHotel } = this.props;
    let hotelData = activeHotel;
    await AuthService.revanista.user.identify();
    const urlParams = new URLSearchParams(window.location.search);
    const dateFromParam = urlParams.get("startDate");
    const dateToParam = urlParams.get("endDate");

    if (dateFromParam || dateToParam) {
      const dateFrom = new Date(dateFromParam || "");
      let dateTo = new Date(dateToParam || "");

      if (
        !isValid(dateFrom) ||
        !isValid(dateTo) ||
        isBefore(new Date(this.state.lastCalendarDate), dateTo) ||
        isBefore(new Date(this.state.lastCalendarDate), dateFrom) ||
        differenceInDays(dateTo, dateFrom) > 31 ||
        differenceInDays(dateTo, dateFrom) < 0
      ) {
        // Invalid date, remove query string and reload
        window.history.replaceState({}, "", window.location.pathname);
        window.location.reload();
        return;
      }

      dateTo = this.calculateDateTo(dateFrom, dateTo);

      this.setState(
        {
          dateFrom: dateFrom,
          dateTo: dateTo,
        },
        this.updateUrlWithDates,
      );
    }

    // Check if activeHotel is an empty object
    if (!activeHotel || Object.keys(activeHotel).length === 0) {
      // If empty object in Redux, fetch from API
      const pathname = window.location.pathname;
      const parts = pathname.split("/");
      const hotelId = parseInt(parts[1]);

      if (isNaN(hotelId)) {
        console.error("Invalid hotel ID in URL");
        return;
      }

      try {
        console.log("Fetching hotel data for hotelId:", hotelId);
        hotelData = await ConfigApi.getHotelConfig(hotelId);
        console.log("hotel data fetched", hotelData);
        this.props.setActiveHotel(hotelData); // Update Redux store
      } catch (error) {
        console.error("Error loading hotel data:", error);
        return;
      }
    }

    try {
      // Check if hotelData is not empty
      if (hotelData && Object.keys(hotelData).length !== 0) {
        const sheetView = await this.computeSheetviews(hotelData);
        this.setState(
          {
            sheetView,
            selectedHotel: hotelData,
          },
          () => {
            this.refreshViewData();
          },
        );

        const lastCalendarDate = await YieldApi.getLastCalendarDate(
          hotelData.hotelId,
        );
        if (lastCalendarDate) {
          this.setState({
            lastCalendarDate: lastCalendarDate,
          });
        }
      }
    } catch (error) {
      console.error("Error computing sheet views:", error);
    }
  }

  updateUrlWithDates = () => {
    const { dateFrom, dateTo } = this.state;
    const url = new URL(window.location.href);
    url.searchParams.set("startDate", format(dateFrom, "yyyy-MM-dd"));
    url.searchParams.set("endDate", format(dateTo, "yyyy-MM-dd"));
    window.history.pushState({}, "", url.toString());
  };

  async computeSheetviews(selectedHotel: any): Promise<SheetView> {
    const rowProp = await computeRowProps(selectedHotel);
    console.log("computed row props", rowProp);
    const s = new SheetView(
      this.props.activeHotel || selectedHotel,
      rowProp,
      this.state.dateFrom,
      this.state.dateTo,
    );
    return s;
  }

  async componentDidUpdate(
    prevProps: Readonly<{
      activeHotel: {
        hotelId: number;
        hotelOnboardDate: string;
        info: { contactEmails: string[] };
      };
    }>,
  ) {
    if (
      prevProps.activeHotel !== this.props.activeHotel &&
      !this.state.yieldSheetPending
    ) {
      this.setState({
        selectedHotel: this.props.activeHotel,
        yieldSheetPending: true,
      });

      try {
        if (this.state.sheetView) {
          const sheetView = await this.computeSheetviews(
            this.props.activeHotel,
          );

          this.setState(
            {
              sheetView: sheetView,
              yieldSheetPending: true,
              selectedHotel: this.props.activeHotel,
            },
            this.refreshViewData,
          );
        }
      } catch (error) {
        console.error("Error refreshing dashboard:", error);
      } finally {
        this.setState({
          yieldSheetPending: false,
          counter: this.state.counter + 1,
        });
      }
    }
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  notifyHotel = () => {
    const notifyPromises: Promise<unknown>[] = [];
    notifyPromises.push(this.state.sheetView.notifyHotel());
    Promise.all(notifyPromises)
      .then((values: any[]) => {
        let c = 0;
        values.forEach((v) => (c += v.count));
        if (c === 0) {
          toast.warn(`no suggested baserate found, no notification sent`);
        } else {
          toast.success(`notification sent for ${c} days`);
        }
      })
      .catch((error) => {
        console.log(error);
        toast.error("Error while notifying hotel");
      });
  };

  async refreshViewData() {
    if (!this.state.sheetView) {
      return;
    }
    // Then refresh the data
    await this.state.sheetView.refreshData();
    this.setState({ yieldSheetPending: false });
    this.setState({ counter: this.state.counter + 1 });
  }

  async changeToToday() {
    await new Promise<void>((resolve) => {
      this.setState(
        {
          dateFrom: new Date(),
          dateTo: endOfMonth(new Date()),
          yieldSheetPending: true,
        },
        async () => {
          const newSheetView = await this.computeSheetviews(
            this.state.selectedHotel,
          );
          this.setState(
            {
              sheetView: newSheetView,
              yieldSheetPending: true,
            },
            async () => {
              await this.refreshViewData();
              this.updateUrlWithDates();
              resolve();
            },
          );
        },
      );
    });
  }

  async changePeriod(daysOffset: number) {
    const currentDate = startOfMonth(
      addMonths(this.state.dateFrom, daysOffset),
    );

    let selectedDate = startOfMonth(addMonths(this.state.dateFrom, daysOffset));
    const twoYearsFromCurrentDate = format(
      endOfMonth(currentDate),
      "yyyy-MM-dd",
    );
    const goingForward = format(addYears(new Date(), 2), "yyyy-MM-dd");

    if (this.state.selectedHotel && this.state.selectedHotel.hotelOnboardDate) {
      selectedDate =
        currentDate < new Date(this.state.selectedHotel.hotelOnboardDate)
          ? new Date(this.state.selectedHotel.hotelOnboardDate)
          : currentDate;
    }

    await new Promise<void>((resolve) => {
      this.setState(
        {
          dateFrom: selectedDate,
          dateTo:
            goingForward < twoYearsFromCurrentDate
              ? endOfMonth(new Date(goingForward))
              : endOfMonth(selectedDate),
          yieldSheetPending: true,
        },
        async () => {
          const newSheetView = await this.computeSheetviews(
            this.state.selectedHotel,
          );
          this.setState(
            {
              sheetView: newSheetView,
              yieldSheetPending: true,
            },
            async () => {
              await this.refreshViewData();
              this.updateUrlWithDates();
              resolve();
            },
          );
        },
      );
    });
  }

  debounce = (func: any, delay: any) => {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout); // Clear previous timeout
    }
    this.debounceTimeout = setTimeout(() => {
      func.apply(this); // Execute the function after the delay
    }, delay);
  };

  setDateFrom = (date: Date | null) => {
    if (!date) return;

    const goingForward = format(addYears(new Date(), 2), "yyyy-MM-dd");

    const dateFrom = date;
    let dateTo = isBefore(new Date(goingForward), endOfMonth(date))
      ? new Date(goingForward)
      : endOfMonth(dateFrom);

    dateTo = this.calculateDateTo(dateFrom, dateTo);

    this.setState({ dateFrom, dateTo, yieldSheetPending: true }, async () => {
      const newSheetView = await this.computeSheetviews(
        this.state.selectedHotel,
      );
      this.setState(
        {
          sheetView: newSheetView,
          yieldSheetPending: true,
        },
        async () => {
          await this.refreshViewData();
          this.updateUrlWithDates();
        },
      );
    });
  };

  setDateTo = (date: Date | null) => {
    if (!date) return;

    const endDate = date;
    const dateTo = this.calculateDateTo(this.state.dateFrom, endDate);

    this.setState({ dateTo, yieldSheetPending: true }, async () => {
      const newSheetView = await this.computeSheetviews(
        this.state.selectedHotel,
      );
      this.setState(
        {
          sheetView: newSheetView,
          yieldSheetPending: true,
        },
        async () => {
          await this.refreshViewData();
          this.updateUrlWithDates();
        },
      );
    });
  };

  togglePublishDropdown = () => {
    this.setState({
      publishDropdownShown: !this.state.publishDropdownShown,
    });
  };

  publishAllInView = async () => {
    this.setState({ yieldSheetPending: true });
    const promiseArray = [];
    if (this.state.selectedHotel.meta.isPublishingInventory) {
      promiseArray.push(this.publishRatesInView(true));
      promiseArray.push(this.publishRestrictionsInView(true));
      promiseArray.push(this.publishAvailabilitiesInView(true));
    } else {
      promiseArray.push(this.publishRatesInView(true));
      promiseArray.push(this.publishRestrictionsInView(true));
    }
    Promise.allSettled(promiseArray).finally(() => {
      this.refreshViewData();
    });
  };

  publishRatesInView = async (publishAll?: boolean) => {
    // Manor and Ash should not be publishing rates
    if (this.state.selectedHotel.features?.canPublish === false) {
      return;
    }
    publishAll = publishAll || false;
    this.setState({ yieldSheetPending: true });
    let toastMessages = {
      pending: "Publishing rates...",
      success: "Published rates",
      error: "Error publishing rates",
    };
    if (process.env.REACT_APP_STAGE !== "production") {
      const isPublishing = await CmApi.publishingHotel(
        this.state.selectedHotel.hotelId,
      );
      if (isPublishing === false) {
        toastMessages = {
          pending: "(f) " + toastMessages.pending,
          success: "(f) " + toastMessages.success,
          error: "(f) " + toastMessages.error,
        };
      }
    }
    await toast.promise(
      YieldApi.publishRatesInView({
        hotelId: this.state.selectedHotel.hotelId,
        start: format(this.state.dateFrom, "yyyy-MM-dd") || "",
        end: this.state.dateTo ? format(this.state.dateTo, "yyyy-MM-dd") : "",
      }),
      toastMessages,
    );
    if (!publishAll) {
      this.refreshViewData();
    }
  };

  publishAvailabilitiesInView = async (publishAll?: boolean) => {
    publishAll = publishAll || false;
    this.setState({ yieldSheetPending: true });
    let toastMessages = {
      pending: "Publishing availabilities...",
      success: "Published availabilities",
      error: "Error publishing availabilities",
    };
    if (process.env.REACT_APP_STAGE !== "production") {
      const isPublishing = await CmApi.publishingHotel(
        this.state.selectedHotel.hotelId,
      );
      if (isPublishing === false) {
        toastMessages = {
          pending: "(f) " + toastMessages.pending,
          success: "(f) " + toastMessages.success,
          error: "(f) " + toastMessages.error,
        };
      }
    }
    await toast.promise(
      YieldApi.publishAvailabilitiesInView({
        hotelId: this.state.selectedHotel.hotelId,
        start: format(this.state.dateFrom, "yyyy-MM-dd") || "",
        end: this.state.dateTo ? format(this.state.dateTo, "yyyy-MM-dd") : "",
      }),
      toastMessages,
    );
    if (!publishAll) {
      this.refreshViewData();
    }
  };

  publishRestrictionsInView = async (publishAll?: boolean) => {
    publishAll = publishAll || false;
    this.setState({ yieldSheetPending: true });
    let toastMessages = {
      pending: "Publishing restrictions...",
      success: "Published restrictions",
      error: "Error publishing restrictions",
    };
    if (process.env.REACT_APP_STAGE !== "production") {
      const isPublishing = await CmApi.publishingHotel(
        this.state.selectedHotel.hotelId,
      );
      if (isPublishing === false) {
        toastMessages = {
          pending: "(f) " + toastMessages.pending,
          success: "(f) " + toastMessages.success,
          error: "(f) " + toastMessages.error,
        };
      }
    }
    await toast.promise(
      YieldApi.publishRestrictions({
        hotelId: this.state.selectedHotel.hotelId,
        start: format(this.state.dateFrom, "yyyy-MM-dd") || "",
        end: this.state.dateTo ? format(this.state.dateTo, "yyyy-MM-dd") : "",
      }),
      toastMessages,
    );
    if (!publishAll) {
      this.refreshViewData();
    }
  };

  getActionList = () => {
    if (!this.state.sheetView || !this.state.sheetView.rowProps) {
      return [];
    }

    const actions = [
      {
        label: "Publish all in view",
        handler: this.publishAllInView,
      },
      {
        label: "Publish rates in view",
        handler: this.publishRatesInView,
      },
      {
        label: "Publish restrictions in view",
        handler: this.publishRestrictionsInView,
      },
    ];

    if (this.state.selectedHotel.meta.isPublishingInventory === true) {
      actions.push({
        label: "Publish availabilities in view",
        handler: this.publishAvailabilitiesInView,
      });
    }

    // Manor and Ash should not be publishing rates
    if (this.state.selectedHotel.features?.canPublish === false) {
      // remove publish rates in view
      actions.splice(1, 1);
    }

    return actions;
  };

  manageBulkUpdatePopUp = (state: boolean) => {
    this.setState({ openBulkUpdate: state });
    this.refreshViewData();
  };

  render() {
    const todayInDateFormat = this.state.dateFrom;
    const activeHotelId = this.state.selectedHotel.hotelId;
    let selectedDate: Date;
    let goingBack;
    const lastCalendarDate = this.state.lastCalendarDate;

    if (this.state.selectedHotel && this.state.selectedHotel.hotelOnboardDate) {
      selectedDate =
        todayInDateFormat > new Date(this.state.selectedHotel.hotelOnboardDate)
          ? this.state.dateFrom
          : new Date(this.state.selectedHotel.hotelOnboardDate);

      goingBack =
        format(todayInDateFormat, "yyyy-MM-dd") <=
        this.state.selectedHotel.hotelOnboardDate
          ? true
          : false;
    } else {
      selectedDate = this.state.dateFrom;
    }

    const daysDifference = differenceInDays(
      endOfMonth(selectedDate),
      selectedDate,
    );

    let endMaxDate = new Date(format(endOfMonth(selectedDate), "yyyy-MM-dd"));

    if (format(addMonths(selectedDate, 1), "yyyy-MM-dd") > lastCalendarDate) {
      endMaxDate = new Date(lastCalendarDate);
    } else if (daysDifference < 15) {
      endMaxDate = new Date(
        format(endOfMonth(addMonths(selectedDate, 1)), "yyyy-MM-dd"),
      );
    }

    const goingForward =
      format(this.state.dateTo, "yyyy-MM-dd") === lastCalendarDate
        ? true
        : false;
    if (
      this.state.dateTo.getMonth() === new Date(lastCalendarDate).getMonth() &&
      this.state.dateTo.getFullYear() ===
        new Date(lastCalendarDate).getFullYear()
    ) {
      this.state.dateTo = new Date(lastCalendarDate);
    }

    return (
      <ThemeProvider theme={baseTheme}>
        <div>
          <div
            style={{
              padding: "44px 0px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                zIndex: 1,
                position: "fixed",
                backgroundColor: "white",
                width: "100%",
                paddingTop: "25px",
                paddingBottom: "23px",
              }}
            >
              <Button
                color="inherit"
                type="button"
                onClick={() => this.changePeriod(-1)}
                disabled={goingBack}
              >
                <ArrowBackIos />
              </Button>

              <div style={{}}>
                <DatePicker
                  portalId="root-portal"
                  className="datePicketDashboard"
                  id="startDatePicker"
                  popperPlacement="bottom-end"
                  selected={selectedDate}
                  onChange={(date) => this.setDateFrom(date)}
                  dateFormat="d LLL yyyy"
                  locale="en-GB"
                  selectsStart={true}
                  startDate={selectedDate}
                  endDate={this.state.dateTo}
                  minDate={
                    this.state.selectedHotel &&
                    this.state.selectedHotel.hotelOnboardDate &&
                    new Date(this.state.selectedHotel.hotelOnboardDate)
                  }
                  maxDate={new Date(lastCalendarDate)}
                />

                <span>—</span>

                <DatePicker
                  portalId="root-portal"
                  className="datePicketDashboard"
                  selected={this.state.dateTo}
                  onChange={(date) => this.setDateTo(date)}
                  id="endDatePicker"
                  dateFormat="d LLL yyyy"
                  locale="en-GB"
                  selectsEnd={true}
                  startDate={selectedDate}
                  endDate={this.state.dateTo}
                  minDate={selectedDate}
                  maxDate={endMaxDate}
                />
              </div>
              <nav>
                <Button
                  color="inherit"
                  type="button"
                  onClick={() => this.changePeriod(+1)}
                  disabled={goingForward}
                >
                  <ArrowForwardIos />
                </Button>
              </nav>
              <span> &nbsp; </span>
              <nav>
                <Tooltip
                  title="Reset date range to current month"
                  arrow
                  placement="top"
                >
                  <Button
                    color="inherit"
                    type="button"
                    onClick={() => this.changeToToday()}
                  >
                    <EventRepeat />
                  </Button>
                </Tooltip>
              </nav>
              {hasPermission(ACTIONS.YIELD_SHEET) && (
                <>
                  <span> &nbsp; </span>
                  <nav>
                    <Button
                      sx={{ marginLeft: "10px" }}
                      variant="contained"
                      color="info"
                      onClick={() => this.setState({ openBulkUpdate: true })}
                      endIcon={<Edit />}
                    >
                      Bulk Update
                    </Button>
                  </nav>
                </>
              )}
              <div
                className={
                  hasPermission(ACTIONS.SUGGESTION) ? "mr-4 ml-auto" : "d-none"
                }
              >
                <Button
                  color="primary"
                  variant="contained"
                  loading={this.state.yieldSheetPending}
                  disabled={this.state.yieldSheetPending}
                  onClick={this.notifyHotel}
                  endIcon={<RoomService />}
                  sx={{ height: 36.75 }}
                >
                  Notify Hotel
                </Button>
                &nbsp;
                {this.state.selectedHotel.features?.canPublish &&
                  hasPermission(ACTIONS.PUBLISH) && (
                    <ButtonGroup
                      variant="contained"
                      ref={this.anchorRef}
                      aria-label="publish actions"
                      sx={{ height: 36.5, boxShadow: "none" }}
                    >
                      <Button
                        color="primary"
                        loading={this.state.yieldSheetPending}
                        onClick={this.publishAllInView}
                        disabled={this.state.yieldSheetPending}
                        sx={{
                          height: "100%",
                        }}
                      >
                        Publish All
                      </Button>
                      <Button
                        color="primary"
                        aria-controls={
                          this.state.publishDropdownShown
                            ? "split-button-menu"
                            : undefined
                        }
                        aria-expanded={
                          this.state.publishDropdownShown ? "true" : undefined
                        }
                        aria-label="select publish action"
                        aria-haspopup="menu"
                        onClick={this.handleToggleDropdown}
                        disabled={this.state.yieldSheetPending}
                        sx={{
                          minWidth: "40px",
                          padding: "6px 10px",
                          height: "100%",
                        }}
                      >
                        <ArrowDropDown />
                      </Button>
                    </ButtonGroup>
                  )}
                <Popper
                  open={this.state.publishDropdownShown}
                  anchorEl={this.anchorRef.current}
                  role={undefined}
                  transition
                  disablePortal
                  placement="bottom-end"
                  sx={{ zIndex: 1000 }}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin:
                          placement === "bottom"
                            ? "center top"
                            : "center bottom",
                      }}
                    >
                      <Paper elevation={3}>
                        <ClickAwayListener
                          onClickAway={this.handleCloseDropdown}
                        >
                          <MenuList id="split-button-menu" autoFocusItem>
                            {this.getActionList()
                              .slice(1)
                              .map((action) => (
                                <MenuItem
                                  key={action.label}
                                  onClick={() => {
                                    action.handler();
                                    this.setState({
                                      publishDropdownShown: false,
                                    });
                                  }}
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                  }}
                                >
                                  {action.label}
                                </MenuItem>
                              ))}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
                &nbsp;
              </div>
            </div>
          </div>
          {!this.state.sheetView ? (
            <Backdrop open={true} invisible={true}>
              <CircularProgress color="inherit" id="circular-progress" />
            </Backdrop>
          ) : this.state.sheetView.days &&
            this.state.sheetView.days.length <= 0 ? (
            <Backdrop open={true} invisible={true}>
              <CircularProgress color="inherit" id="circular-progress" />
            </Backdrop>
          ) : (
            <YieldSheet
              permissions={this.props.user.permissions}
              sheet={this.state.sheetView.days}
              hotel={this.state.sheetView.sheetHotel}
              rowProps={this.state.sheetView.rowProps}
              compset={this.state.sheetView.compset}
              data={this.state.sheetView.sheetData}
              compareOtaRate={this.state.sheetView.compareOtaRate}
              compareDemand={this.state.sheetView.compareDemand}
              onDataUpdate={async () => {
                this.setState({ yieldSheetPending: true });
                await this.delay(3000);
                this.refreshViewData();
              }}
              start={this.state.sheetView.startDate}
              end={this.state.sheetView.endDate}
              activeHotel={this.props.activeHotel}
            />
          )}
          {this.state.openBulkUpdate && (
            <BulkUpdate
              isPopupOpen={this.state.openBulkUpdate}
              popupState={this.manageBulkUpdatePopUp}
            />
          )}
        </div>
      </ThemeProvider>
    );
  }
}

const mapStateToProps = (state: any) => ({
  activeHotel: state.hotelData.activeHotel,
  activeHotelId: state.hotelData.activeHotel?.hotelId,
  user: state.user,
});

const mapDispatchToProps = (dispatch: any) => ({
  setActiveHotel: (hotel: any) => dispatch(setActiveHotel(hotel)),
  resetYieldsheet: () => dispatch(resetYieldsheet()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(Dashboard));
